import type { PortfolioResponse } from "@/adapters/api";
import { Badge } from "@/components/ui/badge.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table.tsx";
import {
	formatCount,
	formatPercent,
	formatPointsValue,
	formatShortDate,
} from "../../utils/format.ts";

const ROW_SKELETONS = ["primeira", "segunda", "terceira", "quarta"];

export interface PortfolioTableProps {
	data?: PortfolioResponse;
	isPending: boolean;
	isError: boolean;
}

export function PortfolioTable({
	data,
	isPending,
	isError,
}: PortfolioTableProps) {
	if (isPending) {
		return (
			<div className="portfolio-skeleton">
				{ROW_SKELETONS.map((key) => (
					<Skeleton key={key} className="portfolio-skeleton__row" />
				))}
			</div>
		);
	}

	if (isError || !data) {
		return (
			<p className="dashboard-panel-error" role="status">
				Não foi possível carregar as obras do canteiro.
			</p>
		);
	}

	if (data.entries.length === 0) {
		return (
			<p className="dashboard-panel-empty" role="status">
				Nenhuma obra neste canteiro.
			</p>
		);
	}

	return (
		<Table className="portfolio-table">
			<TableHeader>
				<TableRow>
					<TableHead>Obra</TableHead>
					<TableHead className="is-numeric">Previsto</TableHead>
					<TableHead className="is-numeric">Realizado</TableHead>
					<TableHead className="is-numeric">Desvio</TableHead>
					<TableHead className="is-numeric">Atrasadas</TableHead>
					<TableHead className="is-numeric">Última medição</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{data.entries.map((entry) => (
					<TableRow key={entry.construction_id}>
						<TableCell className="portfolio-table__name">
							{entry.name}
							{entry.no_baseline && (
								<Badge variant="outline">Sem cronograma</Badge>
							)}
						</TableCell>
						<TableCell className="is-numeric">
							{entry.no_baseline ? "—" : formatPercent(entry.planned_percent)}
						</TableCell>
						<TableCell className="is-numeric">
							{entry.no_baseline ? "—" : formatPercent(entry.actual_percent)}
						</TableCell>
						<TableCell
							className="is-numeric"
							data-tone={
								entry.no_baseline
									? undefined
									: entry.deviation_points < 0
										? "negative"
										: "positive"
							}
						>
							{entry.no_baseline
								? "—"
								: `${formatPointsValue(entry.deviation_points)} p.p.`}
						</TableCell>
						<TableCell className="is-numeric">
							{formatCount(entry.delayed_count)}
						</TableCell>
						<TableCell className="is-numeric">
							{entry.last_measured_on
								? formatShortDate(entry.last_measured_on)
								: "—"}
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	);
}
